
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Routes } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { useEffect } from "react";
import { PublicCommunityProvider } from "./contexts/PublicCommunityContext";
import { CommunityMemberProvider } from "./contexts/CommunityMemberContext";
import { AuthProvider } from "./contexts/AuthContext";
import { BusinessProvider } from "./contexts/BusinessContext";
import { ChatProvider } from "./contexts/ChatContext";
import { PresenceProvider } from "./contexts/PresenceContext";
import { TranslationProvider } from "./contexts/TranslationContext";
import { UserLoggedInPresence } from "./components/UserLoggedInPresence";
import { renderRoutes } from "./routes/routeUtils";
import { SeoProvider } from "./contexts/SeoContext";

type RenderContext = {
  url: string;
  meta: {
    title?: string;
    description?: string;
    ogImage?: string;
    canonical?: string;
    locale?: string;
    siteUrl: string;
    indexable?: boolean;
    siteName?: string;
    jsonLd?: Record<string, unknown>;
  };
  tenant: { 
    name: string;
    primaryColor?: string;
    secondaryColor?: string;
  };
};

interface AppProps {
  isSSR: boolean;
  ssrContext?: RenderContext;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 2,
      retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
      staleTime: 30000,
    },
  },
});

const AppContent = ({ isSSR, ssrContext }: { isSSR: boolean; ssrContext?: RenderContext }) => {
  useEffect(() => {
    console.log('[App] App component mounted');
  }, []);

  // Debug logging for SSR
  if (isSSR) {
    console.log('[App] SSR mode detected');
    console.log('[App] ssrContext:', ssrContext);
  }

  return (
    <>
      <AnimatePresence mode="wait">
        <Routes>
          {renderRoutes()}
        </Routes>
      </AnimatePresence>
    </>
  );
};

const App = ({ isSSR = false, ssrContext }: AppProps) => {
  return (
    <SeoProvider isSSR={isSSR} ssrContext={ssrContext}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <PublicCommunityProvider>
            <TranslationProvider>
              <AuthProvider>
                <CommunityMemberProvider>
                  <BusinessProvider>
                    <PresenceProvider>
                      <ChatProvider>
                        <UserLoggedInPresence />
                        <Toaster />
                        <Sonner />
                        <AppContent isSSR={isSSR} ssrContext={ssrContext} />
                      </ChatProvider>
                    </PresenceProvider>
                  </BusinessProvider>
                </CommunityMemberProvider>
              </AuthProvider>
            </TranslationProvider>
          </PublicCommunityProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </SeoProvider>
  );
};

export default App;
